import React, {
  createContext,
  useCallback,
  use,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

export type ConfirmDialogOptions = Readonly<{
  title: string;
  message: string;
  confirmLabel?: string;
  confirmOnEnter?: boolean;
}>;

type ConfirmFn = (options: ConfirmDialogOptions) => Promise<boolean>;

const ConfirmDialogContext = createContext<ConfirmFn | null>(null);

interface PendingRequest {
  options: ConfirmDialogOptions;
  resolve: (value: boolean) => void;
}

export const ConfirmDialogProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const queueRef = useRef<PendingRequest[]>([]);
  const [current, setCurrent] = useState<PendingRequest | null>(null);

  const closeCurrent = useCallback(() => {
    setCurrent(null);
    if (queueRef.current.length > 0) {
      const next = queueRef.current.shift();
      if (next) setCurrent(next);
    }
  }, []);

  const confirm = useCallback<ConfirmFn>((options) => {
    return new Promise<boolean>((resolve) => {
      const req: PendingRequest = { options, resolve };
      setCurrent((cur) => {
        if (cur === null) {
          return req;
        }
        queueRef.current.push(req);
        return cur;
      });
    });
  }, []);

  const onCancel = useCallback(() => {
    if (current) {
      current.resolve(false);
      closeCurrent();
    }
  }, [current, closeCurrent]);

  const onConfirm = useCallback(() => {
    if (current) {
      current.resolve(true);
      closeCurrent();
    }
  }, [current, closeCurrent]);

  const ctxValue = useMemo(() => confirm, [confirm]);

  const opts: ConfirmDialogOptions | undefined = current?.options;
  const open = Boolean(current);
  const { t } = useTranslation();

  return (
    <ConfirmDialogContext value={ctxValue}>
      {children}
      <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
        {opts ? (
          <DialogTitle>
            <Typography variant="h6">{opts.title}</Typography>
          </DialogTitle>
        ) : null}
        <DialogContent>
          <Typography>{opts ? opts.message : ""}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel}>{t("cancel")}</Button>
          <Button
            onClick={onConfirm}
            variant="contained"
            color="primary"
            autoFocus={opts?.confirmOnEnter}
          >
            {opts?.confirmLabel ?? t("confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmDialogContext>
  );
};

export const useConfirmDialog = (): ConfirmFn => {
  const ctx = use(ConfirmDialogContext);
  if (!ctx) {
    throw new Error(
      "useConfirmDialog must be used within a ConfirmDialogProvider"
    );
  }
  return ctx;
};
